import { FC, useCallback, useContext, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { AppContext } from '../contexts/AppContext';
import styled from 'styled-components';
import Header from '../framework/Header';
import PageTransition from '../components/PageTransition';
import { useLocation } from 'react-router-dom';

/**
 * Small store that lets other views (Autoplay) show a web page in the always-mounted IframeView,
 * without navigating away. The page stays loaded in the background when another view is shown.
 */
let requestedUrl: string | null = null;
let currentOwner: symbol | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach(listener => listener());

/**
 * Show a web page in the IframeView.
 * @returns Owner token to pass to hideIframe
 */
export const showIframe = (url: string): symbol => {
  const owner = Symbol("iframe-owner");
  currentOwner = owner;
  if (requestedUrl !== url) {
    requestedUrl = url;
    notify();
  }
  return owner;
};

/**
 * Hide the IframeView again (the page stays loaded in the background).
 * Ignored if another view has shown the IframeView since, e.g. when the old view unmounts after the new one mounted.
 */
export const hideIframe = (owner: symbol) => {
  if (owner !== currentOwner || requestedUrl === null) { return; }
  currentOwner = null;
  requestedUrl = null;
  notify();
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};
const getRequestedUrl = () => requestedUrl;

const Wrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 100%;
  /* No transform here: it would make the fixed-position menu in Header position relative to this wrapper */
  background-color: #000;
  overflow: hidden;

  /* Kept loaded in the background, but must not be seen or receive input */
  &.inactive {
    visibility: hidden;
    pointer-events: none;
  }

  &.active {
    z-index: 10;
  }
`;

const Iframe = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  width: 100%;
  height: 100%;
  border: none;
`;

interface IframeProps {
  /** Active through the old /iframe/?url= route */
  active?: boolean
}

/**
 * Render a web page in fullscreen. Always mounted, so the page is only loaded once.
 * @returns {JSX.Element} Component template
 */
const IframeView: FC<IframeProps> = ({active: routeActive}) => {
  const { resetTimer, previewIsActive } = useContext(AppContext);
  const { search } = useLocation();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [currentUrl, setCurrentUrl] = useState<string|null>(null);
  const storeUrl = useSyncExternalStore(subscribe, getRequestedUrl);
  const active = Boolean(storeUrl) || Boolean(routeActive);

  // URL requested by another view (Autoplay)
  useEffect(() => {
    storeUrl && setCurrentUrl(storeUrl);
  }, [storeUrl]);

  // URL from the old /iframe/?url= route
  useEffect(() => {
    if (!routeActive) { return; }
    const url = new URLSearchParams(search).get('url');
    url && setCurrentUrl(decodeURIComponent(url));
  }, [search, routeActive]);

  // Reset idle timer if clicked element is the iframe
  const clickCheck = useCallback(() => {
    if (iframeRef.current && document.activeElement === iframeRef.current) {
      resetTimer();
    }
  }, [resetTimer]);

  // Detect blur event - for idle timer reset
  useEffect(() => {
    if (!active || previewIsActive) { return; }
    let refocusTimer: number | undefined;
    const onBlur = () => {
      clickCheck();
      refocusTimer = window.setTimeout(() => { window.focus(); }, 1000);
    };
    window.addEventListener('blur', onBlur);

    return () => {
      window.clearTimeout(refocusTimer);
      window.removeEventListener('blur', onBlur);
    };
  }, [active, clickCheck, previewIsActive]);

  if (!currentUrl) { return null }

  return (
    <Wrapper className={active ? "active" : "inactive"} aria-hidden={!active}>
      {/* Only render the header (and its menu) while visible */}
      {active && <Header/>}
      <PageTransition>
        <Iframe ref={iframeRef} src={currentUrl} title={currentUrl} sandbox="allow-scripts allow-same-origin"/>
      </PageTransition>
    </Wrapper>
  );
}

export default IframeView;
